import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from "react-router-dom";
import { connect } from 'react-redux';
import { Form, Input, Message } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';
import LanguageButton from '../../components/nav/Links/LanguageButton';
import { apiService } from '../../services';
import { fixID, Validators } from '../../helpers';
import { UpdateError, ExelotError } from '../../helpers/fetchErrors';
import { getLanguage } from '../../helpers/getLocationLanguage';

const BuyerPersonalDetailsUpdate = (props) => {
  const {
    getBuyerPersonalDetailsAPI,
    updateBuyerPersonalDetailsAPI,
    match: { params }
  } = props;

  const [submitted, setSubmitted] = useState(false);
  const [parcelId, setParcelId] = useState(null);
  const [parcelData, setParcelData] = useState(null);
  const [updateData, setUpdateData] = useState({
    buyerDetails: { address: {} } }
  );
  const [error, setError] = useState();
  const [success, setSuccess] = useState();
  const [fieldsErrors, setFieldsErrors] = useState({});

  const location = useLocation();
  const history = useHistory();

  const language = useMemo(() => getLanguage(location), [location]);
  const { t } = useTranslation('buyer_details_update');



  useEffect(() => {
    const id = fixID(params.parcelID);
    if (!id) {
      setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      return;
    }

    setParcelId(id);
    const query = 'communicationType=BuyerPersonalDetailsUpdate';

    apiService
      .callServer(getBuyerPersonalDetailsAPI, { ':id:': id }, query, null, null, true)
      .then(res => {
        setParcelData(res);
      })
      .catch(() => {
        setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      });
  }, []);

  const handleOnChange = (e, { value }) => {
    const field = e.target.name;
    updateData.buyerDetails[field] = value;
    setUpdateData({ ...updateData });
  };

  const handleOnChangeAddress = (e, { value }) => {
    const field = e.target.name;
    updateData.buyerDetails.address[field] = value;
    setUpdateData({ ...updateData });
  };

  function getErrorBorderStyle(isError) {
    if (isError) {
      return '1px solid #FF0000';
    }  
  }

  function areFieldsValid() {
    const errObj = {
      phone: Validators.required(updateData.buyerDetails.phone, 'requiredField'),
      stAddress: Validators.required(updateData.buyerDetails.address.stAddress, 'requiredField'),
      city: Validators.required(updateData.buyerDetails.address.city, 'requiredField'),
      zip: Validators.required(updateData.buyerDetails.address.zip, 'requiredField'),
      email: Validators.required(updateData.buyerDetails.email, 'requiredField')
        || Validators.email(updateData.buyerDetails.email, 'invalidEmail'),
    };
    if (parcelData.isIDNeeded) {
      errObj.id = Validators.required(updateData.buyerDetails.id, 'requiredField')
        || Validators.idNumber(updateData.buyerDetails.id, 'invalidId');
    }
    setFieldsErrors(errObj);

    if (Object.values(errObj).find(v => !!v)) {
      return false;
    }

    // if (parcelData.isIDNeeded && !parcelData.buyerDetails.id) {
    //   return false;
    // }

    return true;
  }

  function updateBuyerDetails() {
    const { buyerDetails } = updateData;
    const { contactName, phone, email, id, address } = buyerDetails;
    const { stAddress, stAddress2 = '', city, district = '', zip } = address;
    const body = {
      contactName,
      phone,
      id,
      email,
      address: {
        stAddress,
        stAddress2,
        city,
        district,
        zip
      }
    };
    return apiService
      .callServer(
        updateBuyerPersonalDetailsAPI,
        { ':id:': parcelId },
        null,
        JSON.stringify(body),
        null,
        true
      )
      .catch(() => {
        throw new UpdateError();
      });
  }

  const handleSubmit = () => {
    if (!areFieldsValid()) {
      return;
    }
    setSubmitted(true);

    updateBuyerDetails()
      .then((res) => {
        setSuccess(true);
        setError('');
        setSubmitted(false);
      })
      .catch((err) => {
        setSubmitted(false);
        if (err instanceof ExelotError) {
          setSuccess(false);
          setError(err.message);
          return;
        }

        // Should not happen
        console.error(err);

        // Using a default error message
        setError(new UpdateError().message);
      });
  };

  function renderError() {
    if (!error) {
      return null;
    }

    return (
      <Message
        className='error-message'
        error
        content={error}
      />
    );
  }

  function renderSuccess() {
    if (!success) {
      return null;
    }

    return (
      <div className='animated fadeIn'>
        <div className='section'>
          <h1>
            {t('successHeader')}
          </h1>
          <h2>
            {t('successMsg')}
          </h2>
        </div>
         <div className='section'>
          <div className='title bold'>
            <br></br>
            {t('successTrackMsg')} <a href={`/tracking/${parcelData.hawb}`}>{t('successTrackLink')}</a>
            <br></br>
          </div>
        </div>
      </div>
    );
  }

  if (!parcelData) {
    return renderError();
  }

  const buyer = updateData && updateData.buyerDetails;
  return (
    <>
      <div style={{ marginTop: '20px' }}>
        {/* {isLoading && (
          <Loader size='huge' active />
        )} */}

        <LanguageButton location={location} history={history} language={language} />
      </div>

      <div className={`buyer-details-update ${language}`}>
        {renderError()}
        {
          success 
            ? renderSuccess() 
            : (!parcelData.buyerCanUpdate || parcelData.hoursLeft === null || parcelData.hoursLeft <= 0)
              ? (
                <div className='animated fadeIn'>
                  <div className='box display-linebreak'>
                    {t('pageHasExpired')}
                  </div>
                </div>
              )
              : (
                <div className='animated fadeIn'>
                  <div className='section'>
                    <div className='title bold'>
                      {t('description')}---{parcelData.hoursLeft}
                    </div>
                  </div>
                  <div className='section'>
                    <div className='title bold'>
                      {t('parcel')}
                    </div>
                    <div className='details'>
                      <label>
                        {t('barcode')}:
                      </label>
                      <div className='value'>
                        {parcelData.hawb}
                      </div>
                    </div>
                    <div className='details'>
                      <label>
                        {t('desc')}:
                      </label>
                      <div className='value'>
                        {parcelData.description}
                      </div>
                    </div>
                  </div>
                  <div className='section'>
                    <div className='title bold'>
                      {t('buyerDetails')}
                    </div>
                    <div className='details'>
                      <Form className='sms-buyer-form rtl' onSubmit={handleSubmit}>
                        <Form.Field>
                          <label htmlFor='name'>{t('name')}:</label>
                          <Input type='text' disabled={true} name='name' id='name' value={parcelData.buyerDetails.name} onChange={handleOnChange} style={{ direction: 'ltr' }} />
                        </Form.Field>
                        <Form.Field>
                          <label htmlFor='contactName'>{t('contactName')}:</label>
                          <Input type='text' name='contactName' id='contactName' /*value={updateData.buyerDetails.contactName}*/ onChange={handleOnChange} style={{ direction: 'ltr' }} />
                        </Form.Field>
                        <Form.Field>
                          <label htmlFor='email'>{t('email')}*:</label>
                          <Input type='email' name='email' id='email' /*value={buyer.email}*/ onChange={handleOnChange} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.email)} } />
                        </Form.Field>
                        {fieldsErrors.email &&
                          <div className='error-message animated fadeIn'>{t(fieldsErrors.email)}
                          </div>}
                        <Form.Field>
                          <label htmlFor='phone'>{t('phone')}*:</label>
                          <Input type='text' name='phone' id='phone' /*value={buyer.phone}*/ onChange={handleOnChange} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.phone) }} />
                        </Form.Field>
                        {fieldsErrors.phone &&
                          <div className='error-message animated fadeIn'>{t(fieldsErrors.phone)}
                          </div>}
                        {parcelData.isIDNeeded &&
                          <>
                            <Form.Field>
                              <label htmlFor='id'>{t('id')} *:</label>
                              <Input type='text' name='id' id='id' /*value={buyer.id}*/ onChange={handleOnChange} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.id)}} />
                            </Form.Field>
                            {fieldsErrors.id &&
                              <div className='error-message animated fadeIn'>{t(fieldsErrors.id)}
                              </div>}
                          </>
                        }
                        <Form.Field>
                          <label htmlFor='stAddress'>{t('stAddress')} *:</label>
                          <Input type='text' name='stAddress' id='stAddress' /*value={buyer.address.stAddress}*/ onChange={handleOnChangeAddress} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.stAddress) }} />
                        </Form.Field>
                        {fieldsErrors.stAddress &&
                          <div className='error-message animated fadeIn'>{t(fieldsErrors.stAddress)}
                          </div>}
                        <Form.Field>
                          <label htmlFor='stAddress2'>{t('stAddress')}2:</label>
                          <Input type='text' name='stAddress2' id='stAddress2' /*value={buyer.address.stAddress2}*/ onChange={handleOnChangeAddress} style={{ direction: 'ltr' }} />
                        </Form.Field>
                        <Form.Field>
                          <label htmlFor='city'>{t('city')} *:</label>
                          <Input type='text' name='city' id='city' /*value={buyer.address.city}*/ onChange={handleOnChangeAddress} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.city) }} />
                        </Form.Field>
                        {fieldsErrors.city &&
                          <div className='error-message animated fadeIn'>{t(fieldsErrors.city)}
                          </div>}
                        <Form.Field>
                          <label htmlFor='district'>{t('district')}:</label>
                          <Input type='text' name='district' id='district' /*value={buyer.address.district}*/ onChange={handleOnChangeAddress} style={{ direction: 'ltr' }} />
                        </Form.Field>
                        <Form.Field>
                          <label htmlFor='zip'>{t('zip')} *:</label>
                          <Input type='text' name='zip' id='zip' /*value={buyer.address.zip}*/ onChange={handleOnChangeAddress} style={{ direction: 'ltr', border: getErrorBorderStyle(fieldsErrors.zip) }} />
                        </Form.Field>
                        {fieldsErrors.zip &&
                          <div className='error-message animated fadeIn'>{t(fieldsErrors.zip)}
                          </div>}
                        <div className='centered'>
                          <button className='btn medium orange' type='submit' disabled={submitted}>{t('submit')}</button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              )
          }
      </div>
    </>
  );
};

function mapStateToProps(state) {
  const {
    getBuyerPersonalDetails,
    updateBuyerPersonalDetails
  } = state.api;

  return {
    getBuyerPersonalDetailsAPI: getBuyerPersonalDetails,
    updateBuyerPersonalDetailsAPI: updateBuyerPersonalDetails
  };
}

const BuyerPersonalDetailsUpdateRouter = withRouter(connect(mapStateToProps)(BuyerPersonalDetailsUpdate));
export { BuyerPersonalDetailsUpdateRouter as BuyerPersonalDetailsUpdate };
